import { useEffect, useState } from 'react';
import OTPInput from 'otp-input-react';
import { Typography } from '@mui/material';
import { AivagamButton } from 'components/global/AivagamButton/AivagamButton';


export function OtpVerification({
    OTP,
    setOTP,
    onEdit,
    onVerify,
    onResend,
    onSubmitNewOtp,
    signUpMethod,
    destination,
    t,
    loading,
    resendDelay = 30,
    editDelay = 30,
    expireDelay = 15 * 60,
    editable = true
}) {

    const [enableVerify, setEnableVerify] = useState(false);
    const [resendTimer, setResendTimer] = useState(resendDelay);
    const [editTimer, setEditTimer] = useState(editDelay);
    const [expired, setExpired] = useState(false);
    const [expiryCountdown, setExpiryCountdown] = useState(expireDelay);


    const paraContainer = {
        display: 'flex',
        justifyContent: 'center',
        textAlign: 'center',
        color: 'grey',
    };

    const optInput = {
        border: '1.5px solid #E2E8F0',
        marginRight: '5px',
        height: '3em',
        width: '3em',
        borderRadius: '10px',
        textAlign: 'center',
        color: 'black',
    };

    const buttonContainer = {
        width: "50%",
        fontSize: "1em",
        padding: "0.5em",
        marginBottom: "0.6em",
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    };

    // Use Effects
    useEffect(() => {
        if (resendTimer <= 0) return;
        const interval = setInterval(() => {
            setResendTimer((prev) => prev - 1);
        }, 1000);
        return () => clearInterval(interval);
    }, [resendTimer]);

    useEffect(() => {
        if (editTimer <= 0) return;
        const interval = setInterval(() => {
            setEditTimer((prev) => prev - 1);
        }, 1000);
        return () => clearInterval(interval);
    }, [editTimer]);

    useEffect(() => {
        setEnableVerify(OTP.length === 6 && !expired);
    }, [OTP, expired]);

    useEffect(() => {
        if (expired) return;
        const interval = setInterval(() => {
            setExpiryCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(interval);
                    setExpired(true);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [expired]);

    // Handle Clicks
    const handleVerifyClick = () => {
        onVerify(OTP);
    };

    const handleResendClick = () => {
        setOTP('');
        setResendTimer(resendDelay);
        setEditTimer(editDelay);
        onResend();
    };

    const handleSubmitNewOtp = () => {
        setOTP('');
        setResendTimer(resendDelay);
        setEditTimer(editDelay);
        setExpiryCountdown(expireDelay);
        setExpired(false);
        onSubmitNewOtp();
    };

    const handleEdit = () => {
        setOTP('');
        setResendTimer(resendDelay);
        setEditTimer(editDelay);
        setExpired(false);
        setEnableVerify(false);
        onEdit()
    };

    // Format and show expiry time
    const formatCountdown = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && enableVerify && !loading) {
            handleVerifyClick();
        }
    }

    return (
        <div
            id="otpContainer"
            style={{
                width: '600px',
                padding: '2em',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '8px',
                backgroundColor: '#ffffff',
            }}
        >
            <Typography
                variant="h5"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1em',
                    fontWeight: 600,
                    color: '#1450a0'
                }}
            >
                Enter the verification code
            </Typography>

            <div id='editContainer' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2em' }}>
                <p> {`Code sent to ${signUpMethod === 'phone' ? 'phone number' : 'email address'} ${destination}`} </p>
                {editable && (
                    <button
                        style={{ color: editTimer > 0 ? 'grey' : '#3B82F6', alignSelf: 'flex-end' }}
                        onClick={handleEdit}
                        disabled={editTimer > 0}
                    >
                        {signUpMethod === 'phone' ? 'Edit Number' : 'Edit email'} {editTimer > 0 && `(${editTimer})s`}
                    </button>
                )}
            </div>

            <div
                id='otpInputContainer'
                style={{ display: 'flex', justifyContent: 'center' }}
                onKeyDown={handleKeyDown}
            >
                <OTPInput
                    value={OTP}
                    onChange={setOTP}
                    autoFocus
                    OTPLength={6}
                    otpType="number"
                    disabled={false}
                    inputStyles={optInput}
                />
            </div>

            {!expired && (
                <Typography
                    variant="subtitle2"
                    sx={{
                        marginTop: '1em',
                        display: 'flex',
                        width: '50%',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        alignSelf: 'center',
                        fontWeight: 600,
                        color: 'dimgrey',
                    }}>
                    OTP Expires In: {formatCountdown(expiryCountdown)}
                </Typography>
            )
            }

            <div id='verifyButtonContainer' style={{ ...paraContainer, marginTop: '1em' }}>
                <AivagamButton
                    id='continueButton'
                    buttonText={t('auth.signin.signinMobileOTP.otp.button.verify')}
                    onClick={handleVerifyClick}
                    classStyles={buttonContainer}
                    type="confirm"
                    disabled={!enableVerify || loading}
                />
            </div>

            <div id='resendButtonContainer' style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.4em",
                alignItems: "flex-start"
            }}>
                {expired ? (
                    <p> {t('auth.signin.signinMobileOTP.otp.new.info')} {' '}
                        <button
                            style={{ color: '#3B82F6' }}
                            onClick={handleSubmitNewOtp}
                        >
                            {t('auth.signin.signinMobileOTP.otp.new.button')}
                        </button>
                    </p>
                ) : (
                    <button
                        style={{ color: resendTimer > 0 ? 'grey' : '#3B82F6' }}
                        onClick={handleResendClick}
                        disabled={resendTimer > 0}
                    >
                        Resend Code {resendTimer > 0 && `(${resendTimer})s`}
                    </button>
                )}
            </div>
        </div >
    );
}
